import {ChangeDetectorRef, Component, ElementRef, Inject, Input, OnInit, Optional, Renderer2, ChangeDetectionStrategy} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {takeUntil, tap} from 'rxjs/operators';
import {FormControlsExplicitStates, TypedForm, TypedFormOwnControls} from '../../../../common/form-types';
import {setControlEnabledState} from '../../../../common/form-utils';
import {AccountService} from '../../../../components/accounts';
import {InvPortfolioService} from '../../../../components/investment-portfolio';
import {LeverageService} from '../../../../components/leverage';
import {ModalService} from '../../../../components/modal';
import {OverlayService} from '../../../../components/overlay';
import {Position} from '../../../../components/position';
import {
    formatPrice,
    formatQuantity,
    SymbolDecimalsService,
    SymbolFormats,
    USE_QTY_STORE_FORMAT
} from '../../../../components/symbol-decimals';
import {AccountType} from '../../../../enums/account-type';
import {Side} from '../../../../enums/side';
import {DisplayExchangeAccountFactory} from '../../../../factories/display-exchange-account.factory';
import {ExchangeAccountBase} from '../../../../models/exchange-account';
import {CellSizeObserverService} from '../../../../pages/dashboard/layout/cell/cell-size-observer.service';
import {IS_DETACHED_WIDGET} from '../../../../pages/widget-page/detached-widget-injection';
import {UserService} from '../../../../services/user.service';
import {DisplayQuoteFactory} from '../../../quotes-widget/display-quote.factory';
import {QuotesStreamingService} from '../../quotes-streaming';
import {WidgetOrderConfirmModalComponent} from '../widget-order-confirm-modal';
import {WidgetOrderConfirmModalInfo} from '../widget-order-confirm-modal/widget-order-confirm-modal-common';
import {PosClosingConfirmedParams} from './pos-closing-confirmed-params';

type PosCloseFormValue = PosClosingConfirmedParams;

@Component({
    selector: 'widget-position-close-confirm-modal',
    templateUrl: './widget-pos-close-confirm-modal.component.html',
    styleUrls: ['./widget-pos-close-confirm-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class WidgetPosCloseConfirmModalComponent extends WidgetOrderConfirmModalComponent implements OnInit {

    @Input()
    public position: Position;

    public oppositeSide: Side;

    public form: TypedForm<PosCloseFormValue>;

    public readonly isInvPortfoliosAvailable = this.invPortfolioService.isInvPortfoliosAvailable();

    public isLeverageSelectable: boolean = false;

    public accountBase: ExchangeAccountBase;

    public onPosCloseConfirmCallback: (args: PosCloseFormValue) => void;

    constructor(
        @Optional()
        @Inject(IS_DETACHED_WIDGET)
        public override isDetachedWidget: boolean,

        elementRef: ElementRef,
        modalService: ModalService,
        overlayService: OverlayService,
        quotesStreamingService: QuotesStreamingService,
        cellSizeObserverService: CellSizeObserverService,
        displayQuoteFactory: DisplayQuoteFactory,
        protected override changeDetectorRef: ChangeDetectorRef,
        userService: UserService,
        renderer: Renderer2,
        private symbolDecimalsService: SymbolDecimalsService,
        private accountService: AccountService,
        private invPortfolioService: InvPortfolioService,
        private leverageService: LeverageService
    ) {
        super(
            isDetachedWidget,
            elementRef,
            modalService,
            overlayService,
            quotesStreamingService,
            cellSizeObserverService,
            displayQuoteFactory,
            changeDetectorRef,
            userService,
            renderer
        );
    }

    public override ngOnInit(): void {
        this.widgetOrderConfirmModalInfo = this.createConfirmModalInfo();

        super.ngOnInit();

        this.oppositeSide = this.widgetOrderConfirmModalInfo.side === Side.BUY
            ? Side.SELL
            : Side.BUY;

        this.accountBase = {
            id:   this.position.accountId,
            type: AccountType.MARGIN
        };

        this.initIsLeverageSelectable();

        this.buildForm();

        this.onConfirmCallback = () => {
            const formValue = this.form.getRawValue();

            this.onPosCloseConfirmCallback({
                investmentEntityId: formValue.investmentEntityId || undefined,
                portfolioId:        formValue.portfolioId || undefined,
                leverage:           formValue.leverage || undefined
            });
        };
    }

    private createConfirmModalInfo(): WidgetOrderConfirmModalInfo {
        const CLOSE_POSITION_TITLE: string = 'components.widgets.position-details-widget.close_position';

        const position = this.position;

        const security = this.position.security;
        const symbolFormats: SymbolFormats = this.symbolDecimalsService.getSecurityFormats(security);
        const side: Side = position.quantity < 0 ? Side.SELL : Side.BUY;
        const accountName = DisplayExchangeAccountFactory.getDisplayAccountName(
            this.accountService.getAccountByIdFromCache(position.accountId)
        );

        return {
            title:        CLOSE_POSITION_TITLE,
            side,
            quantity:     formatQuantity(position.quantity, symbolFormats, USE_QTY_STORE_FORMAT),
            price:        formatPrice(0, symbolFormats),
            security,
            accountNames: [accountName],

            isListenToQuotesEnabled: true
        };
    }

    private buildForm(): void {
        const invEntity = this.invPortfolioService.getInvEntity();
        const portfolio = invEntity && this.invPortfolioService.getPortfolio(invEntity.id);

        const fieldsStates: FormControlsExplicitStates<PosCloseFormValue> = {
            investmentEntityId: {
                value:    invEntity && invEntity.id || null,
                disabled: !invEntity
            },
            portfolioId:        {
                value:    portfolio && portfolio.id || null,
                disabled: !portfolio
            },
            leverage:           {
                value:    null,
                disabled: !this.isLeverageSelectable
            }
        };

        const controls: TypedFormOwnControls<PosCloseFormValue> = {
            investmentEntityId: new FormControl(fieldsStates.investmentEntityId),
            portfolioId:        new FormControl(fieldsStates.portfolioId),
            leverage:           new FormControl(fieldsStates.leverage)
        };

        this.form = new FormGroup(controls) as TypedForm<PosCloseFormValue>;

        this.initLeverageValue();
    }

    private initIsLeverageSelectable(): void {
        this.leverageService.isSideLeverageSelectableForSecurityAcc(
            this.position.security,
            this.accountBase,
            this.oppositeSide
        ).pipe(
            tap(isLeverageSelectable => {
                this.isLeverageSelectable = isLeverageSelectable;

                if (this.form) {
                    setControlEnabledState(this.form.controls.leverage, isLeverageSelectable);
                }

                this.changeDetectorRef.markForCheck();
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private initLeverageValue(): void {
        this.leverageService.getSideLeveragesForSecurityAcc(
            this.position.security,
            this.accountBase,
            this.oppositeSide
        ).pipe(
            tap(sideLeverages => {
                const leverage = sideLeverages[0] || null;

                if (leverage && !this.form.controls.leverage.value) {
                    this.form.controls.leverage.setValue(leverage);

                    this.changeDetectorRef.markForCheck();
                }
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }

}
